import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { changePassword, getForgetPasswordToken } from "../API";
import { toast } from "react-toastify";
import AuthLayout, { AuthPasswordInput } from "../components/AuthLayout";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [inputField, setInputField] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordError, setPasswordError] = useState("");
  const [validToken, setValidToken] = useState("");

  const urlToken = location?.pathname?.split("/")[2];

  useEffect(() => {
    if (urlToken) {
      getValidToken(urlToken);
    }
  }, []);

  const resetFields = () => {
    setIsLoading(false);
    setPasswordStrength(0);
    setPasswordError("");
    setValidToken("");
  };

  const getValidToken = async (token) => {
    await getForgetPasswordToken(token).then((res) => {
      setValidToken(res?.data?.token);
    });
  };

  const validatePassword = (password) => {
    const minLength = /.{8,}/;
    const uppercase = /[A-Z]/;
    const specialChar = /[!@#$%^&*(),.?":{}|<>]/;

    let strength = 0;

    if (minLength.test(password)) strength += 1; // Add strength for length
    if (uppercase.test(password)) strength += 1; // Add strength for uppercase
    if (specialChar.test(password)) strength += 1; // Add strength for special character

    setPasswordStrength(strength);

    // Validation errors
    if (strength < 3) {
      if (!minLength.test(password))
        return "Password must be at least 8 characters long.";
      if (!uppercase.test(password))
        return "Password must include at least one uppercase letter.";
      if (!specialChar.test(password))
        return "Password must include at least one special character.";
    }

    return ""; // No error
  };

  const onFieldChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setInputField((previousValue) => ({ ...previousValue, [name]: value }));

    if (name === "password") {
      const error = validatePassword(value);
      setPasswordError(error);
    }
  };

  const onSubmitForm = async () => {
    if (inputField.password === "" || inputField.confirmPassword === "") {
      toast.warning("Please enter the required fields");
    } else if (inputField.password !== inputField.confirmPassword) {
      toast.warning("Passwords do not match");
    } else if (passwordError) {
      toast.warning(passwordError);
    } else {
      setIsLoading(true);
      const payload = {
        data: {
          password: inputField.password,
          confirm_password: inputField.confirmPassword,
        },
        token: validToken,
      };
      await changePassword(payload).then((res) => {
        setIsLoading(false);
        toast.success("Password updated successfully");
        navigate("/");
        resetFields();
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmitForm();
  };

  const strengthColor =
    passwordStrength === 3
      ? "#57d38c"
      : passwordStrength > 1
      ? "#f1cb4b"
      : "#f55050";

  return (
    <AuthLayout>
      <h2 className="auth-title">Set a new password</h2>
      <p className="auth-subtitle">
        Choose a strong password for your WebTime account.
      </p>
      <form noValidate onSubmit={handleSubmit}>
        <AuthPasswordInput
          id="new-password"
          name="password"
          label="New password"
          placeholder="New password"
          autoComplete="new-password"
          onChange={(e) => onFieldChange(e)}
        />
        {passwordError && <small className="auth-error">{passwordError}</small>}
        <div className="auth-strength">
          <small>Password strength: {passwordStrength} / 3</small>
          <div className="auth-strength-track">
            <div
              className="auth-strength-bar"
              style={{
                width: `${(passwordStrength / 3) * 100}%`,
                backgroundColor: strengthColor,
              }}
            />
          </div>
        </div>
        <AuthPasswordInput
          id="confirm-password"
          name="confirmPassword"
          label="Confirm password"
          placeholder="Confirm password"
          autoComplete="new-password"
          onChange={(e) => onFieldChange(e)}
        />
        <div className="auth-actions">
          <button type="submit" className="auth-submit" disabled={isLoading}>
            {isLoading && (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            Reset password
          </button>
          <a href="/" className="auth-forgot">
            Back to log in
          </a>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ChangePassword;
